import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { DashboardFilterType, ProductRatio } from "@/types/dashboard";

import { getDashboardBidFilterToString } from "@/consts/Dashboard";

import { GetMonthlyProductRatioStats } from "@/services/Dashboard";

import { showToast } from "@/utils/toastUtils";

import DashboardCardHeader from "@/components/DashboardCardHeader";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-white border border-gray-200 p-2 rounded shadow text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((entry: any, index: number) => (
        <p key={index} style={{ color: entry.color }} className="leading-4">
          {Number(entry.value).toLocaleString()}%
        </p>
      ))}
    </div>
  );
};

type Props = {
  title: string;
  searchReady: boolean;
  filter: DashboardFilterType;
  onSearchComplete: () => void;
};

export default function DashboardLineChart({
  title,
  searchReady,
  filter,
  onSearchComplete,
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<ProductRatio[]>([]);
  const onLoad = async () => {
    try {
      setIsLoading(true);
      const params = getDashboardBidFilterToString(filter);
      const result = await GetMonthlyProductRatioStats(params);
      if (result.data.success) {
        const newData = result.data.pods[0].content.items.map((item) => ({
          label: `${String(item.year).substring(2)}.${String(item.month).padStart(2, "0")}`,
          value: Math.round(item.ratio * 100),
        }));
        setData(newData);
      } else {
        showToast({
          type: "error",
          message: `${title}을(를) 조회하는데 실패했습니다. ${result.data.error.type}`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: `${title}을(를) 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      setIsLoading(false);
      onSearchComplete();
    }
  };

  useEffect(() => {
    if (searchReady) {
      onLoad();
    }
  }, [searchReady]);
  return (
    <DashboardCardHeader title={title} isLoading={isLoading}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 20, right: 20, bottom: 20, left: 0 }}
        >
          <CartesianGrid vertical={false} horizontal={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 14, fontWeight: 700, fill: "#949494", dy: 10 }}
            axisLine={{ stroke: "#655B96" }}
            tickLine={{ stroke: "#655B96" }}
          />
          <YAxis
            domain={[0, (dataMax: number) => Math.min(dataMax * 1.5, 100)]}
            tickFormatter={(val) => `${val}%`}
            tick={{
              fontSize: 14,
              fontWeight: 700,
              fill: "#949494",
              dx: -10,
            }}
            axisLine={{ stroke: "#655B96" }}
            tickLine={{ stroke: "#655B96" }}
          />
          <Line
            type="linear"
            dataKey="value"
            stroke="#A76D6E"
            strokeWidth={2}
            dot={false}
          />
          <Tooltip content={<CustomTooltip />} />
        </LineChart>
      </ResponsiveContainer>
    </DashboardCardHeader>
  );
}
